import type { TxSummary } from '@apm/contracts';
import { matchesFilter, type RowFilter } from './filter';

/**
 * X-View 좌표 변환 (6회차 결정 2).
 *
 * 가로는 시각(오른쪽이 지금), 세로는 응답시간 **로그 스케일**이다.
 * 응답시간은 몇 ms에서 몇십 초까지 자릿수가 달라 선형으로 그리면 아래에 다 깔린다.
 *
 * 캔버스와 React 어느 쪽에도 매달리지 않는 순수 함수라 그대로 시험할 수 있다.
 */

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface Plot {
  /** CSS 픽셀 기준 캔버스 크기 */
  width: number;
  height: number;
  padding: Padding;
  /** 가로 오른쪽 끝의 시각. epoch ms */
  now: number;
  /** 가로 폭이 담는 시간 */
  windowMs: number;
  /** 세로 아래 끝의 응답시간. 0이면 로그를 못 잡는다 */
  minMs: number;
  /** 세로 위 끝의 응답시간 */
  maxMs: number;
}

export interface Area {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export const DEFAULT_PADDING: Padding = { top: 10, right: 12, bottom: 22, left: 44 };
/** 1ms 아래는 구분해 봐야 의미가 없다 */
export const DEFAULT_MIN_MS = 1;
/** PDF 상한이 30초다. 세로축은 여기서 시작하고 더 느린 것이 오면 늘어난다 */
export const DEFAULT_MAX_MS = 60_000;
/** 세로축 상한을 이 위로는 올리지 않는다. 10분을 넘는 요청은 맨 위에 붙어도 뜻이 통한다 */
const MAX_AXIS_MS = 600_000;

/**
 * 세로축 상한 자동 확장 (후보 #3).
 *
 * 16회차까지 상한이 60초 고정이라 그보다 느린 요청이 전부 맨 위 한 줄에 붙었다.
 * 실시간 연결(수십 분)을 켜면 그 줄만 보였다. 관측된 최댓값을 10의 거듭제곱으로 올려 잡는다.
 * 눈금이 10의 거듭제곱 자리에만 찍히므로(`logTicks`) 축도 그 자리에서 끊는 편이 읽기 쉽다.
 */
export function autoMaxMs(observedMs: number, floorMs: number = DEFAULT_MAX_MS): number {
  if (!Number.isFinite(observedMs) || observedMs <= floorMs) return floorMs;
  const rounded = 10 ** Math.ceil(Math.log10(observedMs));
  return Math.min(MAX_AXIS_MS, Math.max(floorMs, rounded));
}

export function plotArea(plot: Plot): Area {
  const left = plot.padding.left;
  const top = plot.padding.top;
  const right = Math.max(left, plot.width - plot.padding.right);
  const bottom = Math.max(top, plot.height - plot.padding.bottom);
  return { left, top, right, bottom, width: right - left, height: bottom - top };
}

/** 시각 → 가로 좌표. `now`가 오른쪽 끝이다 */
export function xOfTime(plot: Plot, time: number): number {
  const area = plotArea(plot);
  const ratio = (time - (plot.now - plot.windowMs)) / plot.windowMs;
  return area.left + ratio * area.width;
}

export function timeOfX(plot: Plot, x: number): number {
  const area = plotArea(plot);
  if (area.width === 0) return plot.now;
  return plot.now - plot.windowMs + ((x - area.left) / area.width) * plot.windowMs;
}

/** 응답시간 → 세로 좌표. 느릴수록 위로 간다. 범위를 벗어나면 끝에 붙인다 */
export function yOfDuration(plot: Plot, durationMs: number): number {
  const area = plotArea(plot);
  const clamped = Math.min(Math.max(durationMs, plot.minMs), plot.maxMs);
  const span = Math.log10(plot.maxMs) - Math.log10(plot.minMs);
  if (span <= 0) return area.bottom;
  const ratio = (Math.log10(clamped) - Math.log10(plot.minMs)) / span;
  return area.bottom - ratio * area.height;
}

export function durationOfY(plot: Plot, y: number): number {
  const area = plotArea(plot);
  if (area.height === 0) return plot.minMs;
  const ratio = (area.bottom - y) / area.height;
  const span = Math.log10(plot.maxMs) - Math.log10(plot.minMs);
  return 10 ** (Math.log10(plot.minMs) + ratio * span);
}

/** 세로 눈금. 10의 거듭제곱 자리만 찍는다 */
export function logTicks(plot: Plot): number[] {
  const ticks: number[] = [];
  const first = Math.ceil(Math.log10(plot.minMs));
  const last = Math.floor(Math.log10(plot.maxMs));
  for (let exponent = first; exponent <= last; exponent += 1) ticks.push(10 ** exponent);
  return ticks;
}

/**
 * 가로 눈금 간격 (23-A회차, 과거 보기).
 *
 * 22회차까지 가로 창이 늘 5분이라 1분 고정이었다. 과거 보기가 1시간·하루를 담게 되면서
 * 그 값을 그대로 쓰면 눈금 글자가 서로 겹쳐 읽을 수 없다(1시간이면 60개). 창 길이에 맞춰
 * 눈금이 여덟 개를 넘지 않는 첫 간격을 고른다.
 */
const TIME_TICK_STEPS_MS = [
  10_000,
  30_000,
  60_000,
  2 * 60_000,
  5 * 60_000,
  10 * 60_000,
  15 * 60_000,
  30 * 60_000,
  3_600_000,
  2 * 3_600_000,
  3 * 3_600_000,
  6 * 3_600_000,
  12 * 3_600_000,
  24 * 3_600_000,
] as const;

export function timeTickStepMs(windowMs: number, maxTicks = 8): number {
  const first = TIME_TICK_STEPS_MS[0] as number;
  if (!Number.isFinite(windowMs) || windowMs <= 0 || maxTicks <= 0) return first;
  for (const step of TIME_TICK_STEPS_MS) {
    if (windowMs / step <= maxTicks) return step;
  }
  // 이 위(하루 넘는 창)는 눈금이 여덟 개를 넘지 않게 하루 단위로 더 벌린다
  const days = Math.ceil(windowMs / maxTicks / (24 * 3_600_000));
  return days * 24 * 3_600_000;
}

/** 가로 눈금. `stepMs` 간격으로 딱 떨어지는 시각에 찍는다 */
export function timeTicks(plot: Plot, stepMs: number): number[] {
  if (stepMs <= 0) return [];
  const from = plot.now - plot.windowMs;
  const ticks: number[] = [];
  for (let time = Math.ceil(from / stepMs) * stepMs; time <= plot.now; time += stepMs) {
    ticks.push(time);
  }
  return ticks;
}

/** 점 하나가 놓이는 자리. 가로는 **끝난 시각**이다. 오른쪽 끝에서부터 쌓인다 */
export function pointTime(row: TxSummary): number {
  return row.startedAt + row.durationMs;
}

export interface Pick {
  row: TxSummary;
  index: number;
  distance: number;
}

/**
 * 클릭 지점에 가장 가까운 점. `radius`(CSS 픽셀) 밖이면 아무것도 고르지 않는다.
 * 겹친 점 중에서는 나중에 끝난 것이 이긴다(뒤에 그려진 것이 위에 보이므로).
 */
export function pickNearest(
  rows: readonly TxSummary[],
  plot: Plot,
  x: number,
  y: number,
  radius: number,
  filter?: RowFilter,
): Pick | undefined {
  let best: Pick | undefined;
  const limit = radius * radius;
  const area = plotArea(plot);
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] as TxSummary;
    if (filter !== undefined && !matchesFilter(row, filter)) continue;
    const px = xOfTime(plot, pointTime(row));
    // 렌더러는 그림 영역 밖의 점을 그리지 않는다. 안 보이는 점을 고르지 않는다 (44회차)
    if (px < area.left || px > area.right) continue;
    const dx = px - x;
    const dy = yOfDuration(plot, row.durationMs) - y;
    const distance = dx * dx + dy * dy;
    if (distance > limit) continue;
    if (best && distance > best.distance) continue;
    best = { row, index, distance };
  }
  return best ? { ...best, distance: Math.sqrt(best.distance) } : undefined;
}

/** 드래그로 잡은 사각형. CSS 픽셀 기준이고 두 점의 순서는 상관없다 */
export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** 왼쪽 위·오른쪽 아래 순으로 세운다 */
export function normalizeRect(rect: Rect): Rect {
  return {
    x0: Math.min(rect.x0, rect.x1),
    y0: Math.min(rect.y0, rect.y1),
    x1: Math.max(rect.x0, rect.x1),
    y1: Math.max(rect.y0, rect.y1),
  };
}

export interface RectSelection {
  /** 표에 실을 행. 최신순이고 `limit`까지만 담는다 */
  rows: TxSummary[];
  /** 사각형 안에 든 전체 건수. `rows.length`보다 클 수 있다 */
  total: number;
  /** 타입별 합계. 건수 내림차순 */
  byType: { type: TxSummary['txType']; count: number }[];
}

/** 표에 한 번에 실을 최대 행 수. 넘치면 건수만 알린다 */
export const SELECTION_LIMIT = 2_000;

/**
 * 사각형 안에 든 끝난 요청. 5만 점이어도 선형 스캔 한 번이면 된다.
 * 드래그가 끝날 때 한 번만 부른다(프레임마다 부르지 않는다).
 */
export function rowsInRect(
  rows: readonly TxSummary[],
  plot: Plot,
  rect: Rect,
  limit: number = SELECTION_LIMIT,
  filter?: RowFilter,
): RectSelection {
  const box = normalizeRect(rect);
  const area = plotArea(plot);
  const counts = new Map<TxSummary['txType'], number>();
  const hit: TxSummary[] = [];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] as TxSummary;
    if (filter !== undefined && !matchesFilter(row, filter)) continue;
    const x = xOfTime(plot, pointTime(row));
    // 그리지 않은 점(그림 영역 밖)은 끌어 골라도 들지 않는다 (44회차)
    if (x < area.left || x > area.right) continue;
    if (x < box.x0 || x > box.x1) continue;
    const y = yOfDuration(plot, row.durationMs);
    if (y < box.y0 || y > box.y1) continue;
    hit.push(row);
    counts.set(row.txType, (counts.get(row.txType) ?? 0) + 1);
  }
  // 목록은 최근에 끝난 것부터 본다
  hit.sort((a, b) => pointTime(b) - pointTime(a));
  return {
    rows: hit.slice(0, Math.max(0, limit)),
    total: hit.length,
    byType: [...counts]
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)),
  };
}
